import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { REACT_APP_API_URL } from '../config';
import { apiFetch } from '../App';
import './Results.css';

function Results() {
  // Register Chart.js components when the component mounts
  useEffect(() => {
    ChartJS.register(ArcElement, Tooltip, Legend);
    console.log('Chart.js components registered');
    
    return () => {
      // Unregister when component unmounts
      ChartJS.unregister(ArcElement, Tooltip, Legend);
    };
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comparisons, setComparisons] = useState([]);
  const [stats, setStats] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [experiment, setExperiment] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [selectedExperimentId, setSelectedExperimentId] = useState('');
  
  const navigate = useNavigate();
  const { experimentId } = useParams();
  const location = useLocation();

  console.log('Results component rendered. experimentId from params:', experimentId);
  console.log('Current location:', location.pathname);

  // Reset state when component mounts or experimentId changes
  useEffect(() => {
    // Only reset if the experimentId changes to a new value
    if (experimentId !== selectedExperimentId) {
      console.log('Resetting component state due to experimentId change:', experimentId);
      setComparisons([]);
      setStats(null);
      setExperiment(null);
      setLoading(true);
      setError(null);
    }
  }, [experimentId, selectedExperimentId]);

  // Main data loading effect
  useEffect(() => {
    // Fetch all experiments first
    console.log('Initial useEffect - fetching experiments');
    fetchExperiments();
    
    // If we have an experiment ID from URL params, use that
    if (experimentId) {
      console.log('Found experimentId in URL, fetching experiment:', experimentId);
      fetchExperiment(experimentId);
    } else {
      // If no experimentId, we're just showing the dropdown
      setLoading(false);
    }
  }, [experimentId, location.pathname]);

  // Update selected experiment state when experimentId changes
  useEffect(() => {
    if (experimentId && experimentId !== selectedExperimentId) {
      console.log('Setting selected experiment to match URL parameter:', experimentId);
      setSelectedExperimentId(experimentId);
    }
  }, [experimentId, selectedExperimentId]);

  // When selected experiment changes, fetch its data
  useEffect(() => {
    if (selectedExperimentId && !experimentId) {
      console.log('Selected experiment changed to:', selectedExperimentId);
      // Update URL to reflect the selected experiment
      navigate(`/results/${selectedExperimentId}`, { replace: true });
    }
  }, [selectedExperimentId, experimentId, navigate]);

  // Add debug state to track issues
  const [debug] = useState({
    enabled: true,
    log: function(...args) {
      if (this.enabled) console.log('[Debug]', ...args);
    }
  });

  // Define colors matching Python implementation
  const colors = {
    model1: '#1f77b4',  // Blue
    model2: '#006400',  // Dark green
    equallyGood: '#228B22',  // Forest green
    equallyBad: '#d62728',  // Red
  };

  // Add this function to debug response issues
  const debugResponse = async (response, context) => {
    console.log(`${context} status:`, response.status);
    console.log(`${context} headers:`, [...response.headers.entries()]);
    try {
      // Clone the response to avoid consuming it
      const clone = response.clone();
      const text = await clone.text();
      console.log(`${context} body:`, text.substring(0, 500) + (text.length > 500 ? '...' : ''));
      // Try to parse as JSON if it looks like JSON
      if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
        try {
          const data = JSON.parse(text);
          console.log(`${context} parsed JSON:`, data);
          return data;
        } catch (e) {
          console.error(`${context} not valid JSON:`, e);
        }
      }
      return text;
    } catch (e) {
      console.error(`Error examining ${context}:`, e);
      return null;
    }
  };

  const fetchExperiments = async () => {
    try {
      const response = await apiFetch('experiments');
      
      console.log('Experiments response status:', response.status);
      
      if (response.ok) {
        // Examine the response
        const data = await debugResponse(response, 'Experiments response');
        
        if (Array.isArray(data) && data.length === 0) {
          console.log('No experiments found - showing empty state');
          setExperiments([]);
          setLoading(false);
        } else if (data) {
          console.log('Experiments data received:', data);
          setExperiments(data);
        } else {
          setError('Failed to parse experiments data');
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to load experiments:', errorText);
        setError(`Failed to load experiments: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching experiments:', error);
      if (error.name === 'AbortError') {
        setError('Request timed out. The server may be unavailable.');
      } else {
        setError(`Error: ${error.message}`);
      }
    }
  };

  const fetchExperiment = async (id) => {
    try {
      setLoading(true);
      console.log('Fetching experiment details for ID:', id);
      
      const response = await apiFetch(`experiments/${id}`);
      debug.log('Experiment response status:', response.status);
      
      if (response.ok) {
        const experimentData = await response.json();
        debug.log('Experiment data received:', experimentData);
        
        if (!experimentData || !experimentData.model_1 || !experimentData.model_2) {
          debug.log('Warning: Experiment data is missing model information', experimentData);
          setError('Experiment data is incomplete or invalid.');
          setLoading(false);
          return;
        }
        
        // Set experiment data first
        setExperiment(experimentData);
        
        // Once we have the experiment data, fetch its comparisons
        await fetchExperimentComparisons(id);
      } else {
        const errorText = await response.text();
        console.error('Failed to load experiment:', errorText);
        setError(`Failed to load experiment: ${response.status} ${errorText}`);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching experiment:', error);
      setError(`Error: ${error.message}`);
      setLoading(false);
    }
  };

  const fetchExperimentComparisons = async (id) => {
    try {
      console.log('Fetching comparisons for experiment ID:', id);
      
      const response = await apiFetch(`experiments/${id}/comparisons`);
      debug.log('Comparisons response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        debug.log('Comparisons data received:', data.length, 'items');
        
        if (data.length > 0) {
          debug.log('First comparison sample:', data[0]);
        }
        
        setComparisons(data);
        
        // We're assured experiment is already set at this point
        if (experiment) {
          calculateStatistics(data);
        } else {
          debug.log('Warning: Cannot calculate statistics because experiment data is not loaded yet');
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to load comparisons:', errorText);
        setError(`Failed to load comparisons: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching comparisons:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Also add a useEffect to calculate statistics when both experiment and comparisons are loaded
  useEffect(() => {
    if (experiment && comparisons.length > 0) {
      debug.log('Both experiment and comparisons are loaded, calculating statistics');
      calculateStatistics(comparisons);
    }
  }, [experiment, comparisons]);

  const calculateStatistics = (comparisonsData) => {
    console.log('Calculating statistics for', comparisonsData.length, 'comparisons');
    
    const total = comparisonsData.length;
    
    if (!experiment || total === 0) {
      console.log('No experiment data or comparisons, setting empty stats');
      setStats({
        total: 0,
        model1Wins: 0,
        model2Wins: 0,
        equallyGood: 0,
        equallyBad: 0,
        model1WinPercentage: 0,
        model2WinPercentage: 0,
        equallyGoodPercentage: 0,
        equallyBadPercentage: 0
      });
      return;
    }
    
    const model1 = experiment.model_1;
    const model2 = experiment.model_2;
    
    console.log('Counting wins for models:', model1, model2);
    
    // Check first comparison to see what format the result is in
    const firstComparison = comparisonsData[0];
    console.log('Sample comparison:', firstComparison);
    
    // Count wins for each model
    let model1Wins = 0;
    let model2Wins = 0;
    let equallyGood = 0;
    let equallyBad = 0;
    
    comparisonsData.forEach(comparison => {
      const result = comparison.result;
      if (result === model1) {
        model1Wins++;
      } else if (result === model2) {
        model2Wins++;
      } else if (result === 'equally_good') {
        equallyGood++;
      } else if (result === 'equally_bad') {
        equallyBad++;
      } else {
        console.log('Unknown result value:', result);
      }
    });
    
    console.log(`Counted: ${model1Wins} for ${model1}, ${model2Wins} for ${model2}, ${equallyGood} equally good, ${equallyBad} equally bad`);

    const stats = {
      total,
      model1Wins,
      model2Wins,
      equallyGood,
      equallyBad,
      model1WinPercentage: total > 0 ? (model1Wins / total * 100).toFixed(1) : 0,
      model2WinPercentage: total > 0 ? (model2Wins / total * 100).toFixed(1) : 0,
      equallyGoodPercentage: total > 0 ? (equallyGood / total * 100).toFixed(1) : 0,
      equallyBadPercentage: total > 0 ? (equallyBad / total * 100).toFixed(1) : 0
    };
    
    console.log('Statistics calculated:', stats);
    setStats(stats);
  };

  const renderPieChart = () => {
    if (!stats || !experiment) {
      debug.log('Cannot render pie chart - missing stats or experiment data');
      return null;
    }
    
    if (stats.total === 0) {
      return (
        <div className="chart-container">
          <h3>No Comparison Results Yet</h3>
          <p>This experiment doesn't have any completed comparisons.</p>
        </div>
      );
    }

    const data = {
      labels: [
        `Image from ${experiment.model_1}`,
        `Image from ${experiment.model_2}`,
        'Equally good',
        'Equally bad'
      ],
      datasets: [{
        data: [
          stats.model1Wins,
          stats.model2Wins,
          stats.equallyGood,
          stats.equallyBad
        ],
        backgroundColor: [
          colors.model1,
          colors.model2,
          colors.equallyGood,
          colors.equallyBad
        ],
        borderColor: 'white',
        borderWidth: 1
      }]
    };

    const options = {
      plugins: {
        legend: {
          position: 'right',
          labels: {
            font: {
              size: 14
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw;
              const percentage = ((value / stats.total) * 100).toFixed(1);
              return `${context.label}: ${value} (${percentage}%)`;
            }
          }
        }
      },
      cutout: '30%',
      responsive: true,
      maintainAspectRatio: false
    };

    return (
      <div className="chart-container">
        <h3>Distribution of Comparison Results</h3>
        <div className="pie-chart">
          <Pie data={data} options={options} />
        </div>
      </div>
    );
  };

  const renderExperimentSelection = () => {
    return (
      <div className="experiment-selection">
        <label>
          Select Experiment:
          <select 
            value={selectedExperimentId || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              console.log('Selection changed to:', newValue);
              if (newValue) {
                setSelectedExperimentId(newValue);
                // Navigate to the new URL immediately
                navigate(`/results/${newValue}`, { replace: true });
              } else {
                // Handle empty selection
                setSelectedExperimentId('');
                navigate('/results', { replace: true });
              }
            }}
          >
            <option value="">Select an experiment</option>
            {experiments.map(exp => (
              <option key={exp.id} value={exp.id}>{exp.name}</option>
            ))}
          </select>
        </label>
      </div>
    );
  };

  const ApiUnavailableError = () => (
    <div className="api-unavailable">
      <h2>API Service Unavailable</h2>
      <p>The backend API service is not responding. This could be due to:</p>
      <ul>
        <li>The server is not running</li>
        <li>A network connectivity issue</li>
        <li>CORS configuration issues</li>
      </ul>
      <div className="api-troubleshooting">
        <h3>Troubleshooting Steps:</h3>
        <ol>
          <li>Ensure the backend server is running with <code>uvicorn backend.main:app --reload</code></li>
          <li>Check that the API URL in config.js is correct: <code>{REACT_APP_API_URL}</code></li>
          <li>Check browser console for CORS or network errors</li>
        </ol>
        <button onClick={() => window.location.reload()}>
          Refresh Page
        </button>
      </div>
    </div>
  );

  if (loading && !stats) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading results...</p>
      </div>
    );
  }

  // API unavailable error has priority
  if (error && (error.includes('timeout') || error.includes('Failed to fetch'))) {
    return <ApiUnavailableError />;
  }
  
  // Normal error handling
  if (error) {
    return (
      <div className="error-container">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => {
          setError(null);
          setSelectedExperimentId('');
          
          if (experimentId) {
            navigate('/');
          }
        }}>
          {experimentId ? 'Back to Experiments' : 'Try Again'}
        </button>
      </div>
    );
  }

  return (
    <div className="results-container">
      <div className="results-header">
        <h2>
          {experiment 
            ? `Results: ${experiment.name}`
            : 'Experiment Results'
          }
        </h2>
        
        {experiment && (
          <div className="experiment-status">
            <p>
              Status: <span className={`status-${experiment.status}`}>{experiment.status}</span>
            </p>
            <p>
              Progress: {experiment.completed} / {experiment.total} comparisons
            </p>
            {experiment.status !== 'completed' && (
              <Link 
                to={`/compare/${experiment.id}`}
                className="continue-button"
              >
                Continue Experiment
              </Link>
            )}
            <Link 
              to="/"
              className="back-button"
            >
              Back to Experiments
            </Link>
          </div>
        )}
      </div>
      
      {!experimentId && renderExperimentSelection()}

      {stats && experiment && (
        <>
          {renderPieChart()}
          
          <div className="results-stats">
            <h3>Statistics</h3>
            <div className="stats-grid">
              <div className="stat-card">
                <h4>Total Comparisons</h4>
                <p className="stat-value">{stats.total}</p>
              </div>
              
              <div className="stat-card">
                <h4>Preferred {experiment.model_1}</h4>
                <p className="stat-value">{stats.model1Wins}</p>
                <p className="stat-percentage">{stats.model1WinPercentage}%</p>
              </div>
              
              <div className="stat-card">
                <h4>Preferred {experiment.model_2}</h4>
                <p className="stat-value">{stats.model2Wins}</p>
                <p className="stat-percentage">{stats.model2WinPercentage}%</p>
              </div>
              
              <div className="stat-card">
                <h4>Equally Good</h4>
                <p className="stat-value">{stats.equallyGood}</p>
                <p className="stat-percentage">{stats.equallyGoodPercentage}%</p>
              </div>
              
              <div className="stat-card">
                <h4>Equally Bad</h4>
                <p className="stat-value">{stats.equallyBad}</p>
                <p className="stat-percentage">{stats.equallyBadPercentage}%</p>
              </div>
            </div>
          </div>

          <div className="summary-section">
            <button 
              className="summary-toggle"
              onClick={() => setShowSummary(!showSummary)}
            >
              View Summary
            </button>
            {showSummary && (
              <div className="summary-content">
                <h4>Summary:</h4>
                <ul>
                  <li>Images from {experiment.model_1} were preferred in {stats.model1Wins} ({stats.model1WinPercentage}%) comparisons</li>
                  <li>Images from {experiment.model_2} were preferred in {stats.model2Wins} ({stats.model2WinPercentage}%) comparisons</li>
                  <li>Images were equally good in {stats.equallyGood} ({stats.equallyGoodPercentage}%) comparisons</li>
                  <li>Images were equally bad in {stats.equallyBad} ({stats.equallyBadPercentage}%) comparisons</li>
                </ul>
              </div>
            )}
          </div>
        </>
      )}
      
      {!experiment && (
        <div className="no-selection-message">
          <p>Please select an experiment to view results</p>
        </div>
      )}

      {experiment && (
        <div className="actions">
          <button 
            className="action-button"
            onClick={() => navigate('/')}
          >
            Back to Home
          </button>
        </div>
      )}
    </div>
  );
}

export default Results; 